import { supabase } from './supabase';

/**
 * Lecture d'une carte (photo/PDF) par IA VISION multimodale (Edge `extract-menu-vision`,
 * cascade Groq → Gemini). Renvoie le menu au format d'import (### Catégorie / « Nom | PRIX € | Desc »).
 * Vision DIRECTE sur l'image (pas d'OCR) → bien plus fiable sur les cartes graphiques.
 *
 * IMPORTANT — image HD MAIS cappée à 4 Mo : Groq (comme la plupart des API vision)
 * refuse un base64 > 4 Mo. Une photo de téléphone brute fait souvent 5-10 Mo → on la
 * recompresse en JPEG qualité 0.85, côté long max 2400 px. Résolution suffisante pour
 * que le modèle lise le petit texte, taille sous la limite. Trop compresser = flou = erreurs.
 */

/** Convertit un fichier en base64 (sans préfixe data:) + type MIME. Repli si pas une image. */
export async function fileToBase64(file: File): Promise<{ data: string; mediaType: string }> {
  const buf = await file.arrayBuffer();
  const bytes = new Uint8Array(buf);
  let binary = '';
  const CHUNK = 0x8000; // évite « Maximum call stack » sur grosses images
  for (let i = 0; i < bytes.length; i += CHUNK) {
    binary += String.fromCharCode(...bytes.subarray(i, i + CHUNK));
  }
  return { data: btoa(binary), mediaType: file.type || 'image/jpeg' };
}

/** Charge un File image en HTMLImageElement (via object URL, révoqué après). */
function loadImage(file: Blob): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
    img.onerror = (e) => { URL.revokeObjectURL(url); reject(e); };
    img.src = url;
  });
}

/**
 * Exporte un canvas en JPEG base64 sous une taille max (défaut 4 Mo, limite Groq).
 * Part de la qualité 0.85 et la baisse par paliers si besoin (plancher 0.4).
 */
function canvasToCappedBase64(
  canvas: HTMLCanvasElement,
  maxBytes = 4_000_000,
): { data: string; mediaType: string } {
  let quality = 0.85;
  let base64 = '';
  for (let i = 0; i < 6; i++) {
    const dataUrl = canvas.toDataURL('image/jpeg', quality);
    base64 = dataUrl.split(',')[1] ?? '';
    const bytes = Math.floor((base64.length * 3) / 4); // taille décodée approx.
    if (bytes <= maxBytes || quality <= 0.4) break;
    quality -= 0.1;
  }
  return { data: base64, mediaType: 'image/jpeg' };
}

/**
 * Recompresse une PHOTO en JPEG HD cappé (côté long ≤ maxDim, ≤ 4 Mo) pour l'IA vision.
 * Repli sur le fichier brut si le rendu canvas échoue (ex. type exotique).
 */
export async function compressImageForVision(
  file: File,
  { maxDim = 2400, maxBytes = 4_000_000 }: { maxDim?: number; maxBytes?: number } = {},
): Promise<{ data: string; mediaType: string }> {
  try {
    const img = await loadImage(file);
    const longest = Math.max(img.width, img.height) || 1;
    const scale = Math.min(1, maxDim / longest);
    const w = Math.max(1, Math.round(img.width * scale));
    const h = Math.max(1, Math.round(img.height * scale));
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) return fileToBase64(file);
    ctx.drawImage(img, 0, 0, w, h);
    const out = canvasToCappedBase64(canvas, maxBytes);
    return out.data ? out : fileToBase64(file);
  } catch {
    return fileToBase64(file); // repli : envoie le fichier tel quel
  }
}

/**
 * Rend chaque page d'un PDF en IMAGE (base64) via pdf.js, pour la passer à la MÊME IA
 * vision que les photos. Export JPEG cappé à 4 Mo (avant : PNG non borné → pouvait
 * dépasser la limite des API). pdf.js importé dynamiquement → jamais chargé en test.
 */
export async function pdfToImages(data: ArrayBuffer): Promise<{ data: string; mediaType: string }[]> {
  const pdfjs = await import('pdfjs-dist');
  const workerUrl = (await import('pdfjs-dist/build/pdf.worker.min.mjs?url')).default;
  pdfjs.GlobalWorkerOptions.workerSrc = workerUrl;

  const doc = await pdfjs.getDocument({ data }).promise;
  const images: { data: string; mediaType: string }[] = [];
  for (let p = 1; p <= doc.numPages; p++) {
    const page = await doc.getPage(p);
    const viewport = page.getViewport({ scale: 2 }); // x2 : lecture IA plus fiable
    const canvas = document.createElement('canvas');
    canvas.width = viewport.width;
    canvas.height = viewport.height;
    const ctx = canvas.getContext('2d');
    if (!ctx) continue;
    await page.render({ canvasContext: ctx, viewport }).promise;
    const out = canvasToCappedBase64(canvas); // JPEG ≤ 4 Mo
    if (out.data) images.push(out);
  }
  return images;
}

/**
 * Envoie l'image (base64) à l'Edge Function vision et renvoie le texte du menu + le
 * fournisseur qui a répondu (groq / gemini / …) pour l'affichage du badge.
 * Lève une erreur si la fonction échoue (le caller peut alors retomber sur l'OCR).
 */
export async function extractMenuFromImageAI(
  imageBase64: string,
  mediaType: string,
): Promise<{ text: string; provider: string }> {
  const { data, error } = await supabase.functions.invoke('extract-menu-vision', {
    body: { image_base64: imageBase64, media_type: mediaType },
  });
  if (error) throw new Error(error.message);
  const res = data as { ok?: boolean; text?: string; provider?: string; reason?: string } | null;
  if (!res || res.ok === false) throw new Error(res?.reason || 'vision_failed');
  return { text: (res.text ?? '').trim(), provider: res.provider ?? 'ia' };
}
